<script setup lang="ts">
import { reactive } from 'vue'
import { NButton, NCheckbox, NInput, useMessage } from 'naive-ui'
import { inputStatus } from '../composables/inputStatus'
import { useAgentReposStore } from '../stores/agent_repos'

// The "Add a repo" form at the foot of AdminAgentRepos, extracted to keep that
// page under the 300-line limit. It talks to the store directly; the page
// re-renders from the store's repo list once create resolves.
const store = useAgentReposStore()
const message = useMessage()

const addForm = reactive({
  slug: '',
  github_repo: '',
  github_token: '',
  registry_token: '',
  ports: '',
  is_default: false,
  saving: false,
  slugMissing: false,
})

// Comma-separated host ports (#1233) → number[], dropping blanks/non-numeric
// entries rather than rejecting the whole field — the server-side overlap
// check is the real gate; this just avoids submitting garbage.
function parsePorts(raw: string): number[] | undefined {
  const ports = raw
    .split(',')
    .map((p) => Number(p.trim()))
    .filter((p) => Number.isInteger(p) && p > 0)
  return ports.length > 0 ? ports : undefined
}

async function addRepo(): Promise<void> {
  if (!addForm.slug.trim()) {
    addForm.slugMissing = true
    message.error('Slug is required')
    return
  }
  addForm.saving = true
  try {
    await store.create({
      slug: addForm.slug.trim(),
      github_repo: addForm.github_repo.trim() || undefined,
      github_token: addForm.github_token.trim() || undefined,
      registry_token: addForm.registry_token.trim() || undefined,
      ports: parsePorts(addForm.ports),
      is_default: addForm.is_default,
    })
    addForm.slug = ''
    addForm.github_repo = ''
    addForm.github_token = ''
    addForm.registry_token = ''
    addForm.ports = ''
    addForm.is_default = false
    message.success('Repo added')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to add repo')
  } finally {
    addForm.saving = false
  }
}
</script>

<template>
  <section
    class="rounded-2xl border border-border-subtle bg-surface-raised p-4 flex flex-col gap-3"
    data-testid="agent-repo-add-form"
  >
    <h2 class="text-base font-semibold text-ink m-0">Add a repo</h2>
    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <label class="flex flex-col gap-1 text-xs text-ink-muted">
        Slug <span class="text-ink-faint font-normal">(required — matches /workspaces/&lt;slug&gt;)</span>
        <NInput
          v-model:value="addForm.slug"
          placeholder="my-repo"
          :disabled="addForm.saving"
          :status="inputStatus(addForm.slugMissing, addForm.slug)"
          data-testid="agent-repo-add-slug"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-ink-muted">
        GitHub repo <span class="text-ink-faint font-normal">(optional)</span>
        <NInput
          v-model:value="addForm.github_repo"
          placeholder="owner/repo"
          :disabled="addForm.saving"
          data-testid="agent-repo-add-github-repo"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-ink-muted sm:col-span-2">
        Ports
        <span class="text-ink-faint font-normal"
          >(optional — comma-separated host ports this repo's compose file publishes)</span
        >
        <NInput
          v-model:value="addForm.ports"
          placeholder="3040, 8040"
          :disabled="addForm.saving"
          data-testid="agent-repo-add-ports"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-ink-muted sm:col-span-2">
        GitHub PAT <span class="text-ink-faint font-normal">(write-only, optional — can be set later)</span>
        <NInput
          v-model:value="addForm.github_token"
          type="password"
          show-password-on="click"
          placeholder="ghp_…"
          :disabled="addForm.saving"
          data-testid="agent-repo-add-token"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-ink-muted sm:col-span-2">
        Registry token (ghcr.io, classic PAT with read:packages)
        <span class="text-ink-faint font-normal"
          >(write-only, optional — only needed when the compose stack pulls a private ghcr.io image)</span
        >
        <NInput
          v-model:value="addForm.registry_token"
          type="password"
          show-password-on="click"
          placeholder="ghp_…"
          :disabled="addForm.saving"
          data-testid="agent-repo-add-registry-token"
        />
      </label>
    </div>
    <div class="flex items-center justify-between gap-4">
      <NCheckbox v-model:checked="addForm.is_default" :disabled="addForm.saving" data-testid="agent-repo-add-default"
        >Set as default repo</NCheckbox
      >
      <NButton type="primary" :loading="addForm.saving" data-testid="agent-repo-add-submit" @click="addRepo"
        >Add repo</NButton
      >
    </div>
  </section>
</template>
